// Phase 4 Step #10:
// Browser fetch helpers for /api/projects.
//
// These endpoints all require authentication on the server side
// (`requireAuth`), so callers MUST pass a fresh access token via
// `getAccessToken()` before invoking. Anonymous callers will receive
// AuthRequiredError.
//
// Errors mirror generation-api.js (typed, with status + code + details).

import {
    AuthRequiredError,
    GenerationApiError,
    PayloadTooLargeError
} from "./generation-api.js";

export {
    AuthRequiredError,
    GenerationApiError,
    PayloadTooLargeError
};

const DEFAULT_BASE_URL = readBaseUrl();

function readBaseUrl() {
    const value = import.meta.env?.VITE_API_BASE_URL;
    if (typeof value !== "string") return "";
    return value.replace(/\/+$/, "");
}

function buildHeaders(accessToken, hasBody) {
    const headers = {};
    if (hasBody) headers["content-type"] = "application/json";
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    return headers;
}

function pickError(status, body) {
    const code = typeof body?.error === "string" ? body.error : null;
    const message =
        typeof body?.message === "string" && body.message
            ? body.message
            : code || `HTTP ${status}`;

    if (status === 401) return new AuthRequiredError(message, { status, code, details: body });
    if (status === 413) return new PayloadTooLargeError(message, { status, code, details: body });
    return new GenerationApiError(message, { status, code, details: body });
}

async function jsonFetch(url, init) {
    const response = await fetch(url, init);
    let parsed = null;
    try {
        parsed = await response.json();
    } catch {
        parsed = null;
    }
    if (!response.ok) {
        throw pickError(response.status, parsed);
    }
    return parsed;
}

function buildQueryString({ limit, offset } = {}) {
    const params = new URLSearchParams();
    if (Number.isFinite(limit)) params.set("limit", String(limit));
    if (Number.isFinite(offset)) params.set("offset", String(offset));
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}

function rootUrl(baseUrl) {
    return typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
}

export async function listProjects({ accessToken, limit, offset, baseUrl, signal } = {}) {
    const url = `${rootUrl(baseUrl)}/api/projects${buildQueryString({ limit, offset })}`;
    return jsonFetch(url, {
        method: "GET",
        headers: buildHeaders(accessToken, false),
        signal
    });
}

export async function createProject({
    accessToken,
    name,
    projectJson,
    baseUrl,
    signal
} = {}) {
    const url = `${rootUrl(baseUrl)}/api/projects`;
    return jsonFetch(url, {
        method: "POST",
        headers: buildHeaders(accessToken, true),
        body: JSON.stringify({ name, project_json: projectJson }),
        signal
    });
}

export async function getProject({ accessToken, projectId, baseUrl, signal } = {}) {
    const url = `${rootUrl(baseUrl)}/api/projects/${encodeURIComponent(projectId)}`;
    return jsonFetch(url, {
        method: "GET",
        headers: buildHeaders(accessToken, false),
        signal
    });
}

export async function updateProject({
    accessToken,
    projectId,
    fields,
    baseUrl,
    signal
} = {}) {
    const url = `${rootUrl(baseUrl)}/api/projects/${encodeURIComponent(projectId)}`;
    const payload = {};
    if (fields?.name !== undefined) payload.name = fields.name;
    if (fields?.projectJson !== undefined) payload.project_json = fields.projectJson;

    return jsonFetch(url, {
        method: "PUT",
        headers: buildHeaders(accessToken, true),
        body: JSON.stringify(payload),
        signal
    });
}

export async function listProjectRuns({
    accessToken,
    projectId,
    limit,
    offset,
    baseUrl,
    signal
} = {}) {
    const url = `${rootUrl(baseUrl)}/api/projects/${encodeURIComponent(projectId)}/runs${buildQueryString(
        { limit, offset }
    )}`;
    return jsonFetch(url, {
        method: "GET",
        headers: buildHeaders(accessToken, false),
        signal
    });
}

export function bindProjectsApiBootstrap(deps = {}) {
    const {
        bindProjectsClient = null,
        listProjects: listProjectsImpl = listProjects,
        createProject: createProjectImpl = createProject,
        getProject: getProjectImpl = getProject,
        updateProject: updateProjectImpl = updateProject,
        listProjectRuns: listProjectRunsImpl = listProjectRuns,
        getAccessToken = async () => null,
        getSession = async () => null,
        signInWithEmail = async () => {},
        signOut = async () => {},
        openOrganizationProfile = async () => false,
        syncAuthUi = async () => false,
        getAuthClient = () => null,
        subscribeAuthChange = null,
        handleAuthChange = () => {},
        refreshCloudAuthUi = () => {},
        info = () => {},
        warn = () => {}
    } = deps;

    if (typeof bindProjectsClient !== "function") {
        warn("[phase4] window.__bindProjectsClient not present; cloud path disabled");
        return false;
    }

    bindProjectsClient({
        listProjects: listProjectsImpl,
        createProject: createProjectImpl,
        getProject: getProjectImpl,
        updateProject: updateProjectImpl,
        listProjectRuns: listProjectRunsImpl,
        getAccessToken,
        getSession,
        signInWithEmail,
        signOut,
        openOrganizationProfile,
        syncAuthUi
    });

    const notifyAuthChange = (session) => {
        try {
            handleAuthChange(session);
        } catch {
            // Workspace-scoped UI reset is optional.
        }
        try {
            refreshCloudAuthUi();
        } catch {
            // UI not ready.
        }
    };

    if (typeof subscribeAuthChange === "function") {
        Promise.resolve(subscribeAuthChange((session) => {
            notifyAuthChange(session);
        })).catch(() => {
            // Auth listener is optional.
        });
    } else {
        let client = null;
        try {
            client = getAuthClient();
        } catch {
            client = null;
        }
        if (client?.auth?.onAuthStateChange) {
            client.auth.onAuthStateChange((_event, session) => {
                notifyAuthChange(session);
            });
        }
    }
    info("[phase4] /api/projects client bound");
    return true;
}
